/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import useEvent from 'react-use-event-hook';
import { FlexGridFilter } from '@grapecity/wijmo.grid.filter';
import { useEffect, useMemo, useState } from 'react';
import ApproveRequestModal from 'pages/approves/ApproveRequestModal';
import { ApproveRequestPageType } from 'pages/approves/ApproveRequestDetailCase';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { ExcpRole, ExcpRoleSearchParam } from 'models/admin/ExcpRole';
import { useLoading } from 'components/process/Loading';
import { getExcpReqList } from 'apis/admin/ExcpRole';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { PageMode } from 'models/common/Common';
import { useMessageBar } from 'hooks/useMessageBar';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import useSessionStore from 'stores/useSessionStore';
import { findApprovalPageId } from 'apis/approves/approves';
import { useSearchParams } from 'react-router-dom';

const ExcpRoleManagement = () => {
  const initSearchParam = {
    excpNm: '',
    aprReqTitNm: '',
    aprRegProgStatCd: '',
    aprReqProgStatNm: '',
    dataInsUserNm: '',
  };

  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const [isOpenRequestModal, setOpenRequestModal] = useState<boolean>(false);
  const [searchParam, setSearchParam] = useState<ExcpRoleSearchParam>(initSearchParam);
  const [rowData, setRowData] = useState<ExcpRole[]>([]);
  const [statCd, setStatCd] = useState<Code[]>([]);
  const [flexRef, setFlexRef] = useState<any>();
  const [requestModalCondition, setRequestModalCondition] = useState<any>({});
  const { userId, deptCd } = useSessionStore();
  const [searchParams] = useSearchParams();

  const initCodes = async () => {
    const statCd = await getCommonCodeNames('APR_REQ_PROG_STAT_CD');
    setStatCd(statCd);
  };

  useEffect(() => {
    initCodes();
    btnSearch();
  }, []);

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const layoutDefinition = useMemo(
    () => [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 60,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
        align: 'center',
      },
      {
        binding: 'excpNm',
        header: String(t('com.label.협력업체명', '협력업체명')),
        width: '*',
        isReadOnly: true,
        align: 'left',
      },
      {
        binding: 'aprReqTitNm',
        header: String(t('com.label.요청제목', '요청제목')),
        width: 350,
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.aprReqTitNm}</span>',
          click: (e, ctx) => {
            const item = ctx.item;
            if (item?.dataInsUserId) {
              if (item?.aprReqProgStatCd === 'TMP' && item?.dataInsUserId !== userId) {
                openMessageBar({
                  type: 'warning',
                  content:
                    `${item?.dataInsUserNm}` +
                    String(t('com.msg.님이 작성중입니다.', '님이 작성중입니다.')),
                });
                return;
              }
            }
            if (item?.aprReqId) {
              setRequestModalCondition({
                pageId: ApproveRequestPageType.EXCP_AUTH_REQ,
                mode: PageMode.READ,
                aprReqId: item.aprReqId,
              });
              setOpenRequestModal(true);
            }
          },
        }),
      },
      {
        binding: 'aprReqProgStatNm',
        header: String(t('com.label.결재상태', '결재상태')),
        width: '*',
        isReadOnly: true,
        align: 'center',
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.aprReqProgStatCd === 'APV') {
            return `<span class="grey">${item.value}</span>`;
          } else if (item.item.aprReqProgStatCd === 'CCL') {
            return `<span class="purple">${item.value}</span>`;
          } else if (item.item.aprReqProgStatCd === 'INP') {
            return `<span class="yellow">${item.value}</span>`;
          } else if (item.item.aprReqProgStatCd === 'REJ') {
            return `<span class="red">${item.value}</span>`;
          } else if (item.item.aprReqProgStatCd === 'REQ') {
            return `<span class="green">${item.value}</span>`;
          } else if (item.item.aprReqProgStatCd === 'TMP') {
            return `<span class="blue">${item.value}</span>`;
          }
        },
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.요청자', '요청자')),
        width: '*',
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.요청일', '요청일')),
        width: '*',
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('com.label.결재자', '결재자')),
        width: '*',
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'aprAprvDtm',
        header: String(t('com.label.결재완료일시', '결재완료일시')),
        width: '*',
        isReadOnly: true,
        align: 'center',
      },
    ],
    []
  );

  const onInitialized = useEvent((grid) => {
    new FlexGridFilter(grid);
    setFlexRef(grid);
  });

  const openRequestModal = () => {
    setRequestModalCondition({
      pageId: ApproveRequestPageType.EXCP_AUTH_REQ,
      mode: PageMode.WRITE,
      excpId: deptCd,
    });
    setOpenRequestModal(true);
  };

  const btnSearch = () => {
    openLoading(true);
    getExcpReqList(searchParam)
      .then((result: ExcpRole[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: initSearchParam[cur] || '' }),
          {}
        ) as ExcpRoleSearchParam
    );
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('com.label.프로젝트 권한 목록', '프로젝트 권한 목록');
    book.saveAsync('inssueList-Export.xlsx');
  };

  const openRequestReadModal = (pageId: ApproveRequestPageType | string, aprReqId: string) => {
    setRequestModalCondition({
      pageId: pageId,
      mode: PageMode.READ,
      aprReqId: aprReqId,
    });
    setOpenRequestModal(true);
  };

  useEffect(() => {
    const aprReqId = searchParams?.get('aprReqId');
    if (aprReqId && window.location.pathname === '/system/admin/excp/excp-role') {
      openLoading(true);
      findApprovalPageId(aprReqId)
        .then((pageId) => {
          openLoading(false);
          if (pageId) {
            openRequestReadModal(pageId, aprReqId);
          } else {
            openMessageBar({
              type: 'error',
              content: String(t('setup.label.접근권한이 없습니다.', '접근권한이 없습니다.')),
            });
          }
        })
        .finally(() => openLoading(false));
    }
  }, [searchParams?.get('aprReqId')]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('admin.grid.협력업체명', '협력업체명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="excpNm"
                  placeholder={String(
                    t('com.label.협력업체명을 입력해 주세요.', '협력업체명을 입력해 주세요.')
                  )}
                  value={searchParam?.excpNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.결재상태', '결재상태')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={statCd}
                  defaultValue={searchParam?.aprReqProgStatCd}
                  onChange={(value) => {
                    setSearchParam(() => ({ ...searchParam, aprReqProgStatCd: value }));
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.요청제목', '요청제목')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="aprReqTitNm"
                  placeholder={String(
                    t('setup.label.요청명을 입력해 주세요.', '요청명을 입력해 주세요.')
                  )}
                  value={searchParam?.aprReqTitNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('admin.grid.요청자', '요청자')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="dataInsUserNm"
                  placeholder={String(
                    t('setup.label.요청자를 입력해 주세요.', '요청자를 입력해 주세요.')
                  )}
                  value={searchParam?.dataInsUserNm}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.프로젝트 권한 목록', '프로젝트 권한 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        initialized={onInitialized}
        height={530}
      />
      <GlobalBtnGroup>
        <Button css={IconButton.button} className="create" onClick={openRequestModal} disableRipple>
          {t('com.button.신규 권한 신청', '신규 권한 신청')}
        </Button>
      </GlobalBtnGroup>
      {isOpenRequestModal && (
        <ApproveRequestModal
          open={isOpenRequestModal}
          close={() => {
            btnSearch();
            setOpenRequestModal(false);
          }}
          pageId={requestModalCondition?.pageId}
          aprReqId={requestModalCondition?.aprReqId}
          condition={{
            mode: requestModalCondition?.mode,
            aprReqId: requestModalCondition?.aprReqId,
            excpId: requestModalCondition?.excpId,
          }}
        />
      )}
    </>
  );
};

export default ExcpRoleManagement;
